import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../services/api';
import { motion } from 'framer-motion';
import DashboardLayout from '../components/DashboardLayout';
import { Building2, Users, CheckCircle, Crown, UserCog } from 'lucide-react';

const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [tenantAdmins, setTenantAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tenants');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tenantsRes, adminsRes] = await Promise.all([
        adminAPI.tenants.list(),
        adminAPI.users.list(0, 100, null, 'admin')
      ]);
      setTenants(tenantsRes.data);
      setTenantAdmins(adminsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const styles = `
    .page-header {
      margin-bottom: 32px;
    }

    .page-title {
      font-size: 28px;
      font-weight: 800;
      color: hsl(var(--foreground));
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .page-subtitle {
      font-size: 14px;
      color: hsl(var(--muted-foreground));
    }

    .tabs-container {
      display: flex;
      gap: 8px;
      margin-bottom: 32px;
      border-bottom: 2px solid hsl(var(--border));
      padding-bottom: 0;
    }

    .tab-button {
      padding: 12px 24px;
      background: transparent;
      border: none;
      border-bottom: 2px solid transparent;
      font-size: 14px;
      font-weight: 600;
      color: hsl(var(--muted-foreground));
      cursor: pointer;
      transition: all 0.2s;
      margin-bottom: -2px;
    }

    .tab-button:hover {
      color: hsl(var(--foreground));
    }

    .tab-button.active {
      color: hsl(var(--primary));
      border-bottom-color: hsl(var(--primary));
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 20px;
      margin-bottom: 32px;
    }

    .content-card {
      background: var(--gradient-card);
      border-radius: var(--radius-lg);
      border: 1px solid hsl(var(--border));
      box-shadow: var(--shadow-card);
      overflow: hidden;
    }

    .card-header {
      padding: 20px 24px;
      border-bottom: 1px solid hsl(var(--border));
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .card-title {
      font-size: 18px;
      font-weight: 700;
      color: hsl(var(--foreground));
    }

    .data-table {
      width: 100%;
      border-collapse: collapse;
    }

    .data-table thead {
      background: hsl(var(--secondary) / 0.5);
    }

    .data-table th {
      padding: 14px 24px;
      text-align: left;
      font-size: 11px;
      font-weight: 700;
      color: hsl(var(--muted-foreground));
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .data-table tbody tr {
      border-bottom: 1px solid hsl(var(--border));
      transition: background 0.2s;
    }

    .data-table tbody tr:hover {
      background: hsl(var(--secondary) / 0.3);
    }

    .data-table td {
      padding: 16px 24px;
      font-size: 14px;
      color: hsl(var(--foreground));
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
    }

    .empty-icon {
      font-size: 48px;
      opacity: 0.3;
      margin-bottom: 16px;
    }

    .loading-container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: hsl(var(--background));
    }
  `;

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="loading-container">
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
            <div style={{ fontSize: '14px', color: 'hsl(var(--muted-foreground))' }}>
              Loading Super Admin Dashboard...
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <DashboardLayout user={user} logout={logout}>
        <div className="page-header">
          <h1 className="page-title">
            <Crown size={32} strokeWidth={2.5} style={{ color: 'hsl(var(--primary))' }} />
            Super Admin Dashboard
          </h1>
          <p className="page-subtitle">Manage all tenants and tenant administrators</p>
        </div>

        {/* Stats */}
        <motion.div
          className="stats-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="stat-card">
            <div className="icon-container">
              <Building2 size={20} />
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '800', color: 'hsl(var(--foreground))', margin: '16px 0 4px' }}>
              {tenants.length}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Tenants
            </p>
            <div style={{ fontSize: '12px', color: 'hsl(var(--success))', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CheckCircle size={14} />
              {tenants.filter(t => t.is_active).length} Active
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="stat-card">
            <div className="icon-container">
              <Users size={20} />
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '800', color: 'hsl(var(--foreground))', margin: '16px 0 4px' }}>
              {tenantAdmins.length}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Tenant Admins
            </p>
            <div style={{ fontSize: '12px', color: 'hsl(var(--success))', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CheckCircle size={14} />
              {tenantAdmins.filter(a => a.is_active).length} Active
            </div>
          </motion.div>
        </motion.div>

        {/* Tabs */}
        <div className="tabs-container">
          <button
            className={`tab-button ${activeTab === 'tenants' ? 'active' : ''}`}
            onClick={() => setActiveTab('tenants')}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Building2 size={16} />
            Tenants ({tenants.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'admins' ? 'active' : ''}`}
            onClick={() => setActiveTab('admins')}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <UserCog size={16} />
            Tenant Admins ({tenantAdmins.length})
          </button>
        </div>

        {/* Content */}
        {activeTab === 'tenants' && (
          <motion.div
            className="content-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="card-header">
              <h2 className="card-title">All Tenants</h2>
              <button className="btn-primary" onClick={() => navigate('/admin/tenants')}>
                Manage Tenants
              </button>
            </div>

            {tenants.length === 0 ? (
              <div className="empty-state">
                <Building2 size={48} style={{ opacity: 0.3, margin: '0 auto 16px' }} />
                <p style={{ color: 'hsl(var(--muted-foreground))' }}>No tenants found</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Tenant Name</th>
                    <th>Code</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((tenant, index) => (
                    <tr key={tenant.tenant_id}>
                      <td style={{ fontWeight: '600', color: 'hsl(var(--muted-foreground))' }}>
                        {index + 1}
                      </td>
                      <td>
                        <strong>{tenant.tenant_name}</strong>
                        {tenant.description && (
                          <div style={{ fontSize: '12px', color: 'hsl(var(--muted-foreground))', marginTop: '2px' }}>
                            {tenant.description}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="badge badge-secondary">{tenant.tenant_code}</span>
                      </td>
                      <td>
                        {tenant.is_active ? (
                          <span className="status-active">Active</span>
                        ) : (
                          <span className="status-inactive">Inactive</span>
                        )}
                      </td>
                      <td>{new Date(tenant.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </motion.div>
        )}

        {activeTab === 'admins' && (
          <motion.div
            className="content-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="card-header">
              <h2 className="card-title">Tenant Administrators</h2>
              <button className="btn-primary" onClick={() => navigate('/admin/users')}>
                + Manage Users
              </button>
            </div>

            {tenantAdmins.length === 0 ? (
              <div className="empty-state">
                <UserCog size={48} style={{ opacity: 0.3, margin: '0 auto 16px' }} />
                <p style={{ color: 'hsl(var(--muted-foreground))' }}>No tenant admins found</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Tenant</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tenantAdmins.map((admin, index) => (
                    <tr key={admin.user_id}>
                      <td style={{ fontWeight: '600', color: 'hsl(var(--muted-foreground))' }}>
                        {index + 1}
                      </td>
                      <td><strong>{admin.full_name}</strong></td>
                      <td>{admin.username}</td>
                      <td style={{ color: 'hsl(var(--muted-foreground))' }}>{admin.email}</td>
                      <td>
                        {admin.tenant_name ? (
                          <span className="badge badge-secondary">{admin.tenant_name}</span>
                        ) : (
                          <span className="badge badge-primary">Super Admin</span>
                        )}
                      </td>
                      <td>
                        {admin.is_active ? (
                          <span className="status-active">Active</span>
                        ) : (
                          <span className="status-inactive">Inactive</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </motion.div>
        )}
      </DashboardLayout>
    </>
  );
};

export default SuperAdminDashboard;
